import { defineMessages } from 'react-intl';

export const communityTemplateDetailsMessages = defineMessages({
  pdfDownloadError: {
    defaultMessage: 'Download Failed',
    id: 'c0j31PpQrc',
    description: 'Toast title when community template PDF download fails',
  },
  xmlDownloadError: {
    defaultMessage: 'Failed to download the XML. Please try again.',
    id: 'k3E7cqRVIZ',
    description: 'Toast title when downloading a community XML form fails',
  },
  downloadXmlButton: {
    defaultMessage: 'Download XML',
    id: 'i5wF1wswBj',
    description: 'Visible label for downloading a community XML form',
  },
  downloadXmlForTemplate: {
    defaultMessage: 'Download XML for {title}',
    id: 'ZIcS+XIE/4',
    description: 'Accessibility label for downloading a named community XML form',
  },
  xmlEmptyValue: {
    defaultMessage: '—',
    id: 'LDOOGJSpho',
    description: 'Placeholder shown for an unfilled XML form field',
  },
  templateNotFoundToast: {
    defaultMessage: 'Template not found',
    id: 'Ed6u6+Msom',
    description: 'Toast when the requested community template cannot be loaded',
  },
  pageTitleLoading: {
    defaultMessage: 'Loading Template | Heidi Health',
    id: 'kkMBmWvqd2',
    description: 'Browser tab title while community template details are loading',
  },
  pageTitleNotFound: {
    defaultMessage: 'Template not found',
    id: 'dVw95Fj7wj',
    description: 'Browser tab title when community template is missing',
  },
  pageTitleWithTemplate: {
    defaultMessage: '{title} | {specialty} {templateKind} | Heidi Health',
    id: 'AccKvYUgsV',
    description:
      'Browser tab title for a community template; templateKind matches legacy “Template name” label',
  },
  templateKindInTitle: {
    defaultMessage: 'Template name',
    id: '8zwdUVxYsz',
    description:
      'Short label used in the page title after specialty (legacy common.templateName)',
  },
  metaDescriptionFallback: {
    defaultMessage: '{specialty} template for medical documentation.',
    id: 'VGZtww8MmU',
    description:
      'SEO meta description opening when the template has no generated description yet',
  },
  metaDescriptionBody: {
    defaultMessage: '{intro} {createdBy} {author}. {trailing}',
    id: 'u+N2W2QXpf',
    description:
      'SEO meta description for a community template detail page; {createdBy} is the attribution label before the author name',
  },
  metaDescriptionTrailing: {
    defaultMessage:
      'This template helps streamline the documentation process, ensuring no critical information is missed during the initial assessment.',
    id: 'zdLhP2yMoc',
    description: 'Closing sentence of community template SEO meta description',
  },
  defaultAuthorBrand: {
    defaultMessage: 'Heidi Health',
    id: '5USUQs0WYz',
    description: 'Fallback author / brand name when template author is missing',
  },
  metaKeywords: {
    defaultMessage:
      'Medical template, {specialty}, medical documentation, {category}, clinical notes',
    id: 'whBr0KEwAK',
    description: 'Comma-style SEO keywords for a community template',
  },
  backToTemplateCommunity: {
    defaultMessage: 'Template Community',
    id: '0hDogbQy4A',
    description: 'Desktop back button label returning to the template community list',
  },
  importsCount: {
    defaultMessage: '{count, plural, one {# time} other {# times}}',
    id: 'pID6+4uTT8',
    description: 'How many times a community template has been imported (uses column)',
  },
  howToUseNoteParagraph1: {
    defaultMessage:
      'Heidi makes it easy to use templates, ensuring you have great notes and documentation every time. Simply click on <useTemplate>“Use Template”</useTemplate> to open it in Heidi. If you don’t already have an account, you’ll be prompted to create one. The template will then be saved into your library as your own version, allowing you to make adjustments or rename it as you please. If you’re part of a team, you can share the template with your colleagues. Please note that to avoid duplicates, you can’t publish anything you’ve taken from the community back into the community.',
    id: 'ZurD+z9mlK',
    description: 'How to use this template — paragraph 1 (note / document templates)',
  },
  howToUseNoteParagraph2: {
    defaultMessage:
      'Once the template is in your library, you can select it after you finish recording to use it as your note template or set it as your default note in settings. You can also access the template to create additional outputs by selecting it after pressing <createAction>“Create.”</createAction> To find the template quickly, simply search for it or add it to your favorites within the templates library page.',
    id: 'EvtVf6Mhsh',
    description: 'How to use this template — paragraph 2 (note / document templates)',
  },
  howToUseNoteParagraph3: {
    defaultMessage:
      'Experience the difference that Heidi’s templates can make in your note-taking and documentation process. Transform the way you capture and organize information, and unlock your full productivity potential with Heidi’s intuitive template system.',
    id: 'So/hkQk8x/',
    description: 'How to use this template — paragraph 3 (note / document templates)',
  },
  howToUseFormParagraph1: {
    defaultMessage:
      'Heidi makes it easy to use forms, ensuring you have great documentation every time. Simply click on <useTemplate>“Use Template”</useTemplate> to open it in Heidi. If you don’t already have an account, you’ll be prompted to create one. The form will then be saved into your library as your own version, allowing you to rename it as you please. If you’re part of a team, you can share the form with your colleagues. Please note that to avoid duplicates, you can’t publish anything you’ve taken from the community back into the community.',
    id: 'P5JHr6PIml',
    description: 'How to use this form template — paragraph 1',
  },
  howToUseFormParagraph2: {
    defaultMessage:
      'Once the form is in your library, you can select it after you finish recording. Heidi will then auto-fill your form based on the information captured during your session. You can also access the form to create additional outputs by selecting it after pressing <createAction>“Create.”</createAction> To find the form quickly, simply search for it or add it to your favorites within the templates library page.',
    id: 'BF26eJx+gr',
    description: 'How to use this form template — paragraph 2',
  },
  howToUseFormParagraph3: {
    defaultMessage:
      'Experience the difference that Heidi’s forms can make in your documentation process. Transform the way you capture and organize information, and unlock your full productivity potential with Heidi’s intuitive form system.',
    id: 'c3+qam7jv+',
    description: 'How to use this form template — paragraph 3',
  },
  emailShareSubject: {
    defaultMessage: 'Heidi Template to check out',
    id: '4e3jgVi1YT',
    description: 'Default subject line for sharing a community template by email',
  },
  emailShareBody: {
    defaultMessage: 'Great template I found on Heidi: ',
    id: 'XpWf7glg1h',
    description: 'Opening line of the body when sharing a community template by email',
  },
  linkCopiedToast: {
    defaultMessage: 'Copied!',
    id: 'buu7wBU2d4',
    description: 'Toast after copying the template share URL to the clipboard',
  },
  createdByLabel: {
    defaultMessage: 'Created by',
    id: 'zCbWfdWArZ',
    description: 'Label above the template author on community template details',
  },
  specialtyLabel: {
    defaultMessage: 'Specialty',
    id: 'PgSaPeCXUN',
    description: 'Label above the specialty on community template details',
  },
  lastEditedLabel: {
    defaultMessage: 'Last edited',
    id: '319A42zGn9',
    description: 'Label above the last edited date on community template details',
  },
  usesLabel: {
    defaultMessage: 'Uses',
    id: 'j4hUa0M729',
    description: 'Label above import/use count on community template details',
  },
  tagsHeading: {
    defaultMessage: 'Tags',
    id: 'B1CoykolU/',
    description: 'Heading for specialty and category badges',
  },
  shareHeading: {
    defaultMessage: 'Share',
    id: 'ahjQXVte3w',
    description: 'Heading for share actions (copy link, LinkedIn, email)',
  },
  expandLabel: {
    defaultMessage: 'Expand',
    id: 'vtklLZG5Ms',
    description: 'Label for expanding the embedded form preview',
  },
  openFormPreviewFullscreen: {
    defaultMessage: 'Open form preview in fullscreen',
    id: 'Q5YdVQY0vX',
    description: 'Accessibility label and tooltip for fullscreen form preview control',
  },
  downloadPdfForTemplate: {
    defaultMessage: 'Download PDF for {title}',
    id: 'KDHEzu6kQ9',
    description: 'Accessibility label and tooltip for downloading the template PDF',
  },
  downloadPdfButton: {
    defaultMessage: 'Download PDF',
    id: 'EDDzzYxf8R',
    description:
      'Visible label for the download PDF button on community template details',
  },
  tryFillFormButton: {
    defaultMessage: 'Try this form',
    id: '75Fd7pH2SX',
    description:
      'Visible label for the button that scrolls to the form-fill demo section',
  },
  aboutThisTemplate: {
    defaultMessage: 'About this template',
    id: 'IbKwz22WL8',
    description: 'Section heading above the template description',
  },
  generatingDescription: {
    defaultMessage: 'We’re working on generating the description for this template.',
    id: 'EmYjmsrKJl',
    description: 'Placeholder copy when a community template has no description yet',
  },
  previewTemplate: {
    defaultMessage: 'Preview template',
    id: 'jR26EBRU2Q',
    description: 'Section heading above the template preview area',
  },
  exampleNoteTab: {
    defaultMessage: 'Example note',
    id: '9pwbpJO3j8',
    description: 'Tab label for example note output in template preview',
  },
  templateStructureTab: {
    defaultMessage: 'Template structure',
    id: 'kHsugz5oq5',
    description: 'Tab label for template structure in preview',
  },
  exampleNoteDisclaimer: {
    defaultMessage:
      'Note: The example note contains fictional names and content for demonstration purposes only.',
    id: 'lWQDNqVC/b',
    description: 'Disclaimer under the example note tab',
  },
  howToUseHeading: {
    defaultMessage: 'How to use',
    id: 'xW9xWhoNPW',
    description: 'Section heading for instructions on using the template',
  },
  anonymousAuthor: {
    defaultMessage: 'Anonymous',
    id: '1HqYl3Jxfq',
    description: 'Shown when the community template author name is blank',
  },
});
